import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NotificationProvider = ({ children }) => {
  const { isAuthenticated, getAuthHeader } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [desktopPermission, setDesktopPermission] = useState('default');
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationsEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });

  // Save notification preference to localStorage
  useEffect(() => {
    localStorage.setItem('notificationsEnabled', JSON.stringify(notificationsEnabled));
  }, [notificationsEnabled]);

  // Toggle notifications on/off
  const toggleNotifications = useCallback(async (enabled) => {
    if (enabled && desktopPermission !== 'granted') {
      // Request permission if enabling
      const permission = await requestDesktopPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        return true;
      }
      return false;
    }
    setNotificationsEnabled(enabled);
    return true;
  }, [desktopPermission]);

  // Request desktop notification permission (with timeout for Edge compatibility)
  const requestDesktopPermission = useCallback(async () => {
    if ('Notification' in window) {
      try {
        const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve('denied'), 3000));
        const permission = await Promise.race([
          Notification.requestPermission(),
          timeoutPromise
        ]);
        setDesktopPermission(permission);
        return permission;
      } catch {
        return 'denied';
      }
    }
    return 'denied';
  }, []);

  // Show desktop notification
  const showDesktopNotification = useCallback((title, body, onClick) => {
    // Only show if both permission granted AND notifications enabled
    if (notificationsEnabled && desktopPermission === 'granted' && 'Notification' in window) {
      const notification = new Notification(title, {
        body,
        icon: 'https://customer-assets.emergentagent.com/job_request-flow-12/artifacts/nazg9foa_United%20Group%20PNG.png',
        badge: 'https://customer-assets.emergentagent.com/job_request-flow-12/artifacts/nazg9foa_United%20Group%20PNG.png',
        tag: 'united-group-ticket',
        requireInteraction: false
      });

      if (onClick) {
        notification.onclick = onClick;
      }

      setTimeout(() => notification.close(), 5000);
    }
  }, [desktopPermission, notificationsEnabled]);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await fetch(`${API}/notifications`, {
        headers: getAuthHeader(),
        
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Fetch notifications error:', error);
    }
  }, [isAuthenticated, getAuthHeader]);

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await fetch(`${API}/notifications/unread-count`, {
        headers: getAuthHeader(),
        
      });

      if (response.ok) {
        const data = await response.json();
        const newCount = data.count;
        
        // Show desktop notification if new notifications arrived
        if (newCount > unreadCount) {
          // Fetch latest notification to show specific message
          const notifResponse = await fetch(`${API}/notifications`, {
            headers: getAuthHeader(),
            
          });
          
          if (notifResponse.ok) {
            const notifications = await notifResponse.json();
            const latestUnread = notifications.find(n => !n.is_read);
            
            if (latestUnread) {
              showDesktopNotification(
                latestUnread.title || 'Admin Desk Notification',
                latestUnread.message || 'You have a new notification',
                () => {
                  window.focus();
                  if (latestUnread.ticket_id) {
                    window.location.href = `/tickets/${latestUnread.ticket_id}`;
                  }
                }
              );
            }
          }
        }
        
        setUnreadCount(newCount);
      }
    } catch (error) {
      console.error('Fetch unread count error:', error);
    }
  }, [isAuthenticated, getAuthHeader, unreadCount, showDesktopNotification]);

  // Mark as read
  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API}/notifications/${notificationId}/read`, {
        method: 'PUT',
        headers: getAuthHeader(),
        
      });

      setNotifications(prev =>
        prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Mark as read error:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await fetch(`${API}/notifications/read-all`, {
        method: 'PUT',
        headers: getAuthHeader(),
        
      });

      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Mark all as read error:', error);
    }
  };

  // Check desktop permission on mount
  useEffect(() => {
    if ('Notification' in window) {
      setDesktopPermission(Notification.permission);
    }
  }, []);

  // Poll for notifications
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
      fetchUnreadCount();

      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 30000); // Poll every 30 seconds

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchNotifications, fetchUnreadCount]);

  const value = {
    notifications,
    unreadCount,
    desktopPermission,
    notificationsEnabled,
    toggleNotifications,
    requestDesktopPermission,
    showDesktopNotification,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
